import { CircleHelp } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isPostCodeRequiredForCountry } from '@/shared/config/countries'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InstallationCountrySelect } from '@/features/installations/components/installation-country-select'
import { InstallationFormRequiredMark } from '@/features/installations/components/installation-create/installation-form-required-mark'
import { InstallationFormMessage } from '@/features/installations/components/installation-form-message'
import { InstallationUnlocodeInput } from '@/features/installations/components/installation-unlocode-input'
import type { InstallationCreateFormInput } from '@/features/installations/installation-create.schema'

type InstallationCreateStepAddressLatinProps = {
  form: UseFormReturn<InstallationCreateFormInput>
  unlocodeCountryFilter: string | null
}

export function InstallationCreateStepAddressLatin({
  form,
  unlocodeCountryFilter,
}: InstallationCreateStepAddressLatinProps) {
  const { t } = useTranslation('installations')
  const country = useWatch({ control: form.control, name: 'country' })
  const postCodeRequired = isPostCodeRequiredForCountry(country ?? '')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('create.sections.addressLatin')}</CardTitle>
        <CardDescription>{t('create.sections.addressLatinHint')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="country"
          render={({ field, fieldState }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>
                {t('create.fields.country.label')}
                <InstallationFormRequiredMark />
              </FormLabel>
              <FormControl>
                <InstallationCountrySelect
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  placeholder={t('create.fields.country.placeholder')}
                  disabled
                  aria-invalid={fieldState.invalid}
                />
              </FormControl>
              <FormDescription>
                {t('create.fields.country.sameAsRegistrationNote')}
              </FormDescription>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-start">
          <FormField
            control={form.control}
            name="street"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1">
                <FormLabel>
                  {t('create.fields.street.label')}
                  <InstallationFormRequiredMark />
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="streetNumber"
            render={({ field }) => (
              <FormItem className="w-full shrink-0 sm:w-40">
                <FormLabel>
                  {t('create.fields.streetNumber.label')}
                  <InstallationFormRequiredMark />
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="postCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('create.fields.postCode.label')}
                {postCodeRequired ? <InstallationFormRequiredMark /> : null}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('create.fields.city.label')}
                <InstallationFormRequiredMark />
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="poBox"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('create.fields.poBox.label')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="longitude"
          render={({ field }) => {
            const coordinateValue =
              typeof field.value === 'number' && Number.isFinite(field.value)
                ? String(field.value)
                : ''
            return (
              <FormItem>
                <FormLabel>{t('create.fields.longitude.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    name={field.name}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    value={coordinateValue}
                    onChange={(event) => {
                      const raw = event.target.value
                      if (raw === '') {
                        field.onChange(0)
                        return
                      }
                      field.onChange(Number.parseFloat(raw))
                    }}
                  />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )
          }}
        />
        <FormField
          control={form.control}
          name="latitude"
          render={({ field }) => {
            const coordinateValue =
              typeof field.value === 'number' && Number.isFinite(field.value)
                ? String(field.value)
                : ''
            return (
              <FormItem>
                <FormLabel>{t('create.fields.latitude.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    name={field.name}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    value={coordinateValue}
                    onChange={(event) => {
                      const raw = event.target.value
                      if (raw === '') {
                        field.onChange(0)
                        return
                      }
                      field.onChange(Number.parseFloat(raw))
                    }}
                  />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )
          }}
        />
        <FormField
          control={form.control}
          name="unlocode"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel className="flex items-center gap-1.5">
                {t('create.fields.unlocode.label')}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={t('create.fields.unlocode.tooltip')}
                    >
                      <CircleHelp className="size-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm text-balance">
                    {t('create.fields.unlocode.tooltip')}
                  </TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <InstallationUnlocodeInput
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  placeholder={t('create.fields.unlocode.placeholder')}
                  countryCodeFilter={unlocodeCountryFilter}
                  disabled={field.disabled}
                />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}
